import React, { useEffect, useState } from "react";
import { Receipt } from "lucide-react";
import { PageTitle } from "@/components/SettingsPanel";
import { CurrencyCards, DateRange, EntriesList, type StatementData } from "@/components/Statement";
import { fmtMoney } from "@/components/EventCard";
import { StatementPdfButton } from "@/components/StatementPdf";
import { api } from "@/lib/api";
import { usePrefs } from "@/contexts/PrefsContext";
import { Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import type { User } from "@/types";
import { fmtDate, fmtDay } from "@/lib/time";
import { useLiveTick } from "@/lib/live";

type Data = StatementData & {
  fee_rule: { model?: string; per_person: { amount: string; currency: string }; effective_from: string; exempt: boolean; exemption_reason: string } | null;
  launch_exemption_end: string;
};

export default function VenueFinance() {
  const receipt = useSearchParams()[0].get("receipt");
  const { t, lang } = usePrefs();
  const [range, setRange] = useState({ from: "", to: "" });
  const [d, setD] = useState<Data | null>(null);
  const me = useAuth().user as User;
  const allowed = ["owner", "finance"].includes(me.venue_role || "");
  const tick = useLiveTick();
  useEffect(() => {
    if (!allowed) return;
    const q = new URLSearchParams(Object.entries(range).filter(([, v]) => v)).toString();
    api.get(`/venue/finance/statement${q ? `?${q}` : ""}`).then((r) => setD(r.data)).catch(() => setD(null));
  }, [tick, range, allowed]);
  useEffect(() => {
    if (receipt && d) document.getElementById(`entry-${receipt}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [receipt, d]);
  const r = d?.fee_rule;
  if (!allowed) return <Navigate to="/venue" replace />;
  const lastDay = d ? new Date(new Date(d.launch_exemption_end).getTime() - 1).toISOString() : "";
  return (
    <div data-testid="venue-finance-page">
      <PageTitle title={t("b7.myFinance")} sub={t("b7.myFinanceSub")} />
      {d && (
        <div data-testid="venue-fee-rule" className="rounded-3xl border bg-card p-5 mb-6 flex gap-3 items-start">
          <Receipt className="h-5 w-5 text-primary shrink-0 mt-0.5" />
          <div className="text-sm space-y-1">
            <p className="font-bold">{t("b7.feeRule")}: {r && r.model === "free_trial" ? <>{t("fm.free_trial")} · {t("admin.trialEnds").replace("{date}", fmtDay(((r as any).trial_end || "").slice(0, 10), lang))}</> : r && r.model && r.model !== "none" ? <><bdi>{fmtMoney(r.per_person as any)}</bdi> · {t(`fm.${r.model}`)}</> : t("b7.noRule")}
              {r && <span className="font-normal text-muted-foreground"> · {t("b7.since")} {fmtDate(r.effective_from, lang)}</span>}
              {r?.exempt && <span className="ms-2 rounded-full bg-emerald-500/15 text-emerald-700 px-2 py-0.5 text-xs">{t("b7.exemptRule")}</span>}
            </p>
            <p className="text-muted-foreground">{t("b7.launchUntil")} {fmtDate(lastDay, lang)}</p>
          </div>
        </div>
      )}
      <div className="flex flex-wrap items-end justify-between gap-2">
        <DateRange onApply={(from, to) => setRange({ from, to })} />
        <div className="mb-4"><StatementPdfButton fileId={me.venue_account_id || "venue"} endpoint="/venue/finance/statement" name={me.venue_account?.business_name || "venue"} /></div>
      </div>
      {d && <><CurrencyCards data={d} /><EntriesList entries={d.entries} name={me.venue_account?.business_name || ""} highlight={receipt} /></>}
    </div>
  );
}
